import { type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import PhotoDetails, { type TagType } from '../PhotoDetails.tsx';
import PhotoDetailsSection from '../PhotoDetailsSection.tsx';
import PhotosSearchBrowseFirmDetailsRenderer from '../PhotosSearchBrowse/PhotosSearchBrowseFirmDetailsRenderer.tsx';
import RelatedPhotosRendererSuspense from '../RelatedPhotos/RelatedPhotosRendererSuspense.tsx';
import { useTrackSearchBrowsePhotoPageview } from '../helpers/interiorsPhotosTracking.ts';
import classnames from 'classnames';

import styles from './PhotosSearchBrowseModalContainer.scss';

import { type PhotosSearchBrowseModalContainer_photo$key } from './__generated__/PhotosSearchBrowseModalContainer_photo.graphql.ts';
import { InteriorBillboardAd } from '../DisplayAds/InteriorBillboardAd.tsx';
import { PHOTO_TOP_PLACEMENT } from 'dibs-google-ad-manager/exports/constants.ts';

type Props = {
    onClose: () => void;
    isOpen: boolean;
    isDesktop?: boolean;
    photo: PhotosSearchBrowseModalContainer_photo$key | null | undefined;
    showRail?: boolean;
    showRelatedPhotos?: boolean;
    fillBackground?: boolean;
    isMobile?: boolean;
    isLoading?: boolean;
    preferredStyleTag?: TagType | null;
    userId: string;
    children: ReactNode;
    areDisplayAdsEnabled?: boolean | null;
};

const PhotosSearchBrowseModalContainer: FC<Props> = ({
    children,
    onClose,
    isOpen,
    isDesktop = false,
    isMobile = false,
    isLoading,
    photo: photoRef,
    showRail = true,
    fillBackground,
    preferredStyleTag,
    showRelatedPhotos = true,
    areDisplayAdsEnabled,
    userId,
}) => {
    const photo = useFragment(
        graphql`
            fragment PhotosSearchBrowseModalContainer_photo on PhotosSearchBrowsePhoto {
                ...PhotosSearchBrowseFirmDetailsRenderer_searchBrowsePhoto
                ...interiorsPhotosTracking_searchBrowsePhoto
                name
                path
                serviceId
                styles {
                    name
                    value
                    urlLabel
                }
                room {
                    value
                    name
                    urlLabel
                }
            }
        `,
        photoRef
    );

    const roomTag = photo?.room;
    const photoRoomName = roomTag?.name || '';
    const photoName = photo?.name || photoRoomName || '';
    const styleTag = preferredStyleTag || photo?.styles?.[0];

    useTrackSearchBrowsePhotoPageview(photo);

    const photoDetails = (
        <PhotoDetails styleTag={styleTag} roomTag={roomTag} photoName={photoName} isInModal />
    );

    const firmDetails = <PhotosSearchBrowseFirmDetailsRenderer searchBrowsePhoto={photo} />;

    let content = null;

    if (showRail) {
        if (isDesktop) {
            content = (
                <PhotoDetailsSection>
                    {photoDetails}
                    {firmDetails}
                </PhotoDetailsSection>
            );
        } else {
            content = (
                <>
                    {photoDetails}
                    <PhotoDetailsSection>{firmDetails}</PhotoDetailsSection>
                </>
            );
        }
    }

    return (
        <ModalContainer
            dataTn="photo-modal-container"
            isOpen={isOpen}
            onClose={onClose}
            modalSize="fullscreen"
        >
            {isOpen && (
                <div
                    className={classnames(styles.wrapper, {
                        [styles.fillBackground]: fillBackground,
                    })}
                >
                    {children}
                    {content}
                    {areDisplayAdsEnabled && (
                        <InteriorBillboardAd
                            placement={PHOTO_TOP_PLACEMENT}
                            isDesktop={isDesktop}
                        />
                    )}
                    {!isLoading && showRelatedPhotos && (
                        <RelatedPhotosRendererSuspense
                            photoId={photo?.serviceId || ''}
                            isMobile={isMobile}
                            styleTag={styleTag}
                            roomTag={roomTag}
                            userId={userId}
                            isDesktop={isDesktop}
                            areDisplayAdsEnabled={areDisplayAdsEnabled}
                        />
                    )}
                </div>
            )}
        </ModalContainer>
    );
};

export default PhotosSearchBrowseModalContainer;
